import React, { useEffect, useMemo, useState } from "react";
import { Link as RouterLink, useParams, useSearchParams } from "react-router-dom";
import { DataGrid } from "@mui/x-data-grid";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { useGetNflPlayerByIdQuery } from "../../store/api/nflApi";
import PlayerHeadshot from "../../components/PlayerHeadshot";
import PlayerBioSummary from "../../components/PlayerBioSummary";
import CareerArcChart from "../../components/charts/CareerArcChart";
import { getResultsGridSx } from "../../utils/resultsGrid";
import { brandOutlinedButtonSx } from "../../utils/brandAssets";
import {
  NFL_STAT_GROUPS,
  buildNflSeasonChartPoints,
  buildNflSeasonRows,
  formatNflStatValue,
  getNflAvailableStatGroups,
  getNflCareerHighlightFields,
  getNflCareerTeamAbbrs,
  getNflChartStats,
  getNflComparePath,
  getNflDefaultChartStat,
  getNflDefaultStatGroup,
  getNflDisplayName,
  getNflGridFields,
  getNflScopedCareer,
  getNflSeasonColumns,
  playerHasHiddenCflSeasons,
  playerHasNflSeasons,
} from "../../adapters/nflPlayerAdapter";
import { useNflLeagueScope } from "../../hooks/useNflLeagueScope";
import NflIncludeCflToggle from "../../components/nfl/NflIncludeCflToggle";
import SimilarSeasons from "../../components/similarSeasons/SimilarSeasons";
import {
  eligibleNflSimilarSeasons,
  NFL_SIMILAR_ROLES,
  parseNflSimilarSearch,
  serializeNflSimilarSearch,
} from "../../utils/nflSimilarSeasonsPage";

const StatBlock = ({ label, value }) => (
  <Paper variant="outlined" sx={{ p: 2, textAlign: "center" }}>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Typography variant="h5" fontWeight={800} sx={{ fontVariantNumeric: "tabular-nums" }}>
      {value ?? "—"}
    </Typography>
  </Paper>
);

const NflPlayerDetails = () => {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data: player, isLoading, error } = useGetNflPlayerByIdQuery(id, {
    skip: !id,
  });
  const [groupId, setGroupId] = useState(null);
  const [chartStat, setChartStat] = useState(null);
  const [seasonSortModel, setSeasonSortModel] = useState([
    { field: "year", sort: "desc" },
  ]);
  const { leagueScope, includeCfl, setIncludeCfl } = useNflLeagueScope();

  const availableGroups = useMemo(
    () => getNflAvailableStatGroups(player),
    [player]
  );

  useEffect(() => {
    if (!player) return;
    const defaultGroup = getNflDefaultStatGroup(player);
    setGroupId(defaultGroup);
    setChartStat(getNflDefaultChartStat(defaultGroup));
  }, [player]);

  const activeGroupId =
    availableGroups.some((group) => group.id === groupId)
      ? groupId
      : availableGroups[0]?.id || "passing";

  const similarSearch = useMemo(
    () => parseNflSimilarSearch(searchParams),
    [searchParams]
  );
  const similarRole = NFL_SIMILAR_ROLES.has(similarSearch.role)
    ? similarSearch.role
    : NFL_SIMILAR_ROLES.has(activeGroupId)
    ? activeGroupId
    : undefined;
  const similarSeasonOptions = useMemo(
    () => eligibleNflSimilarSeasons(player, similarRole),
    [player, similarRole]
  );
  const selectedSimilarSeason = similarSeasonOptions.includes(similarSearch.season)
    ? similarSearch.season
    : similarSeasonOptions[0];
  const showSimilarSeasons = Boolean(similarRole && selectedSimilarSeason);

  const chartStats = getNflChartStats(activeGroupId);
  const activeChartStat = chartStats.some((stat) => stat.id === chartStat)
    ? chartStat
    : getNflDefaultChartStat(activeGroupId);
  const activeChartMeta =
    chartStats.find((stat) => stat.id === activeChartStat) || chartStats[0];

  const seasonSortedField = seasonSortModel[0]?.field;
  const seasonRows = useMemo(
    () => buildNflSeasonRows(player, activeGroupId, leagueScope),
    [player, activeGroupId, leagueScope]
  );
  const seasonColumns = useMemo(
    () => getNflSeasonColumns(activeGroupId, seasonSortedField),
    [activeGroupId, seasonSortedField]
  );
  const chartPoints = useMemo(
    () => buildNflSeasonChartPoints(player, activeChartStat, activeGroupId, leagueScope),
    [player, activeChartStat, activeGroupId, leagueScope]
  );

  const handleGroupChange = (_, nextGroup) => {
    if (!nextGroup) return;
    setGroupId(nextGroup);
    setChartStat(getNflDefaultChartStat(nextGroup));
    if (NFL_SIMILAR_ROLES.has(nextGroup)) {
      const nextOptions = eligibleNflSimilarSeasons(player, nextGroup);
      const currentSeason = selectedSimilarSeason;
      const nextSeason = nextOptions.includes(currentSeason)
        ? currentSeason
        : undefined;
      setSearchParams(serializeNflSimilarSearch({ season: nextSeason, role: nextGroup }));
    } else {
      const next = new URLSearchParams(searchParams);
      next.delete("season");
      next.delete("role");
      setSearchParams(next);
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !player) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">Player not found.</Alert>
      </Container>
    );
  }

  const career = getNflScopedCareer(player, activeGroupId, leagueScope);
  const bio = player.bio || player.bioData;
  const hasSeasons = playerHasNflSeasons(player);
  const fieldLabels = Object.fromEntries(
    getNflGridFields(activeGroupId).map((field) => [field.id, field.label])
  );
  const fieldFormats = Object.fromEntries(
    getNflGridFields(activeGroupId).map((field) => [field.id, field.format])
  );
  const careerHighlights = getNflCareerHighlightFields(activeGroupId);
  const hiddenCfl = playerHasHiddenCflSeasons(player) && seasonRows.length === 0;

  return (
    <Container maxWidth="lg" sx={{ py: 2 }}>
      <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap", alignItems: "flex-start", mb: 4 }}>
        <PlayerHeadshot playerId={player.playerId} sport="nfl" size={120} />
        <Box sx={{ flex: 1, minWidth: 240 }}>
          <Typography variant="overline" color="text.secondary">
            NFL Player
          </Typography>
          <Typography variant="h3" fontWeight={800}>
            {getNflDisplayName(player)}
          </Typography>
          {bio?.position && (
            <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
              {bio.position}
            </Typography>
          )}
          {bio?.pageType === "playoff" && (
            <Typography variant="caption" color="warning.main" display="block" sx={{ mt: 1 }}>
              Playoff stats only — full career page pending rescrape
            </Typography>
          )}
          <Button
            component={RouterLink}
            to={getNflComparePath([player.playerId])}
            variant="outlined"
            sx={{ ...brandOutlinedButtonSx, mt: 2 }}
          >
            Compare
          </Button>
        </Box>
      </Box>

      {availableGroups.length > 0 ? (
        <Grid container spacing={2} sx={{ mb: 4 }}>
          {careerHighlights.map((fieldId) => (
            <Grid item xs={6} sm={4} md={2} key={fieldId}>
              <StatBlock
                label={fieldLabels[fieldId] || fieldId.toUpperCase()}
                value={formatNflStatValue(career[fieldId], fieldFormats[fieldId])}
              />
            </Grid>
          ))}
        </Grid>
      ) : null}

      {!hasSeasons && availableGroups.length === 0 ? (
        <Alert severity="info">No season log in this player's archive.</Alert>
      ) : (
        <>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", mb: 1 }}>
            <Tabs value={activeGroupId} onChange={handleGroupChange}>
              {(availableGroups.length > 0 ? availableGroups : NFL_STAT_GROUPS).map(
                (group) => (
                  <Tab key={group.id} value={group.id} label={group.label} />
                )
              )}
            </Tabs>
            <NflIncludeCflToggle includeCfl={includeCfl} onToggle={setIncludeCfl} />
          </Box>

          {hiddenCfl ? (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              CFL seasons hidden
            </Typography>
          ) : seasonRows.length === 0 ? (
            <Alert severity="info" sx={{ mb: 3 }}>
              No {activeGroupId} season rows in this player's archive.
            </Alert>
          ) : (
            <>
              <Paper sx={{ p: 2, mb: 3 }}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    gap: 2,
                    flexWrap: "wrap",
                    mb: 2,
                  }}
                >
                  <Typography variant="h6" fontWeight={700}>
                    {activeChartMeta?.label} by season
                  </Typography>
                  <FormControl size="small" sx={{ minWidth: 160 }}>
                    <InputLabel>Stat</InputLabel>
                    <Select
                      value={activeChartStat}
                      label="Stat"
                      onChange={(event) => setChartStat(event.target.value)}
                    >
                      {chartStats.map((stat) => (
                        <MenuItem key={stat.id} value={stat.id}>
                          {stat.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
                <CareerArcChart
                  namedSeries={[
                    {
                      name: getNflDisplayName(player),
                      points: chartPoints,
                    },
                  ]}
                  yAxisName={activeChartMeta?.label}
                  emptyMessage={`No season values for ${activeChartMeta?.label} in this player's log.`}
                />
              </Paper>

              <Paper sx={{ overflow: "hidden" }}>
                <DataGrid
                  rows={seasonRows}
                  columns={seasonColumns}
                  loading={false}
                  disableRowSelectionOnClick
                  disableColumnMenu
                  hideFooter
                  sortingMode="client"
                  sortModel={seasonSortModel}
                  onSortModelChange={setSeasonSortModel}
                  rowHeight={52}
                  sx={getResultsGridSx()}
                />
              </Paper>
            </>
          )}
        </>
      )}

      {showSimilarSeasons && (
        <Box sx={{ mt: 3 }}>
          <SimilarSeasons
            league="nfl"
            playerId={id}
            season={selectedSimilarSeason}
            seasons={similarSeasonOptions}
            role={similarRole}
            onSeasonChange={(season) =>
              setSearchParams(
                serializeNflSimilarSearch({ season, role: similarRole })
              )
            }
          />
        </Box>
      )}

      <Paper variant="outlined" sx={{ p: 3, mt: 3 }}>
        <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
          Bio
        </Typography>
        <PlayerBioSummary
          sport="nfl"
          bio={{
            ...bio,
            teams: getNflCareerTeamAbbrs(player, leagueScope).join(", "),
          }}
        />
      </Paper>
    </Container>
  );
};

export default NflPlayerDetails;
